import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  TablePagination,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { getAllUsers, updateUserAdminStatus, deleteUser } from '../../../firebase/services/userService';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import BaseDialog from '../../../components/common/BaseDialog';
import BaseButton from '../../../components/common/BaseButton';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null, // 'admin' ou 'delete'
    userId: null,
    username: '',
    isAdmin: false
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [operationStatus, setOperationStatus] = useState({
    success: false,
    error: false,
    message: ''
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

  // Configuration du style moderne sobre
  const glassBoxSx = {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)',
    }
  };

  // Charger tous les utilisateurs
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        const allUsers = await getAllUsers();
        setUsers(allUsers);
        setFilteredUsers(allUsers);
      } catch (err) {
        addSystemNotification('Erreur lors du chargement des utilisateurs:', 'error');
        setError("Impossible de charger la liste des utilisateurs.");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Filtrer les utilisateurs
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      const displayName = user.displayName || '';
      const email = user.email || '';

      return displayName.toLowerCase().includes(searchLower) || 
             email.toLowerCase().includes(searchLower);
    });

    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleAdmin = (user) => {
    setConfirmDialog({
      open: true,
      type: 'admin',
      userId: user.id,
      username: user.displayName || user.email || 'Utilisateur',
      isAdmin: !user.isAdmin
    });
  };

  const handleDeleteUser = (user) => {
    setConfirmDialog({
      open: true,
      type: 'delete',
      userId: user.id,
      username: user.displayName || user.email || 'Utilisateur',
      isAdmin: false
    });
  };

  const handleConfirmAction = async () => {
    const { type, userId, isAdmin } = confirmDialog;

    try {
      if (type === 'admin') {
        await updateUserAdminStatus(userId, isAdmin);
        
        // Mettre à jour la liste des utilisateurs
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId ? { ...user, isAdmin } : user
          )
        );

        setOperationStatus({
          success: true,
          error: false,
          message: `Les droits d'administrateur ont été ${isAdmin ? 'accordés' : 'retirés'} avec succès.`
        });
      } else if (type === 'delete') {
        await deleteUser(userId);
        
        // Retirer l'utilisateur de la liste
        setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));

        setOperationStatus({
          success: true,
          error: false,
          message: 'Utilisateur supprimé avec succès.'
        });
      }
    } catch (err) {
      addSystemNotification('Erreur lors de l\'opération', 'error');
      setOperationStatus({
        success: false,
        error: true,
        message: `Erreur: Impossible d'effectuer cette opération.`
      });
    } finally {
      setConfirmDialog({ open: false, type: null, userId: null, username: '', isAdmin: false });
      
      // Fermer l'alerte après 5 secondes
      setTimeout(() => {
        setOperationStatus({
          success: false,
          error: false,
          message: ''
        });
      }, 5000);
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, type: null, userId: null, username: '', isAdmin: false });
  };

  const getUserAvatar = (user) => {
    const name = user.displayName || user.email || 'U';
    const firstLetter = name.charAt(0).toUpperCase();
    
    return (
      <Avatar
        sx={{
          bgcolor: user.isAdmin ? '#3F51B5' : '#9e9e9e',
          color: 'white',
          width: 40,
          height: 40,
          fontWeight: 600,
          fontSize: '1.1rem'
        }}
      >
        {firstLetter}
      </Avatar>
    );
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 400,
        ...glassBoxSx,
        m: 3
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2, color: '#3F51B5' }} />
          <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
            Chargement des utilisateurs...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          m: 3,
          borderRadius: '12px',
          backgroundColor: 'white',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          boxShadow: '0 4px 16px rgba(239, 68, 68, 0.08)'
        }}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête */}
      <Box sx={{ 
        ...glassBoxSx,
        mb: 3,
        p: 3,
        backgroundColor: '#f8fafc',
        border: '1px solid rgba(63, 81, 181, 0.1)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
            boxShadow: '0 6px 20px rgba(63, 81, 181, 0.3)',
          }}>
            <GroupIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Box>
            <Typography 
              variant="h5" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                color: '#1f2937',
                mb: 0.5
              }}
            >
              👥 Gestion des Utilisateurs
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} • Gérez les permissions et comptes
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Messages de statut */}
      {operationStatus.success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            borderRadius: '12px',
            backgroundColor: 'white',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            boxShadow: '0 4px 16px rgba(34, 197, 94, 0.08)'
          }}
        >
          ✅ {operationStatus.message}
        </Alert>
      )}
      
      {operationStatus.error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: '12px',
            backgroundColor: 'white',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            boxShadow: '0 4px 16px rgba(239, 68, 68, 0.08)'
          }}
        >
          ❌ {operationStatus.message}
        </Alert>
      )}
      
      {/* Barre de recherche */}
      <Box sx={{ ...glassBoxSx, mb: 3, p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="🔍 Rechercher un utilisateur par nom ou email..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#3F51B5' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#f8fafc',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              '&:hover': {
                backgroundColor: 'white',
                borderColor: 'rgba(63, 81, 181, 0.3)',
              },
              '&.Mui-focused': {
                backgroundColor: 'white',
                borderColor: '#3F51B5',
                boxShadow: '0 4px 12px rgba(63, 81, 181, 0.15)',
              }
            }
          }}
        />
      </Box>
      
      {/* Tableau des utilisateurs */}
      <Box sx={glassBoxSx}>
        <TableContainer sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: '#f8fafc',
                borderBottom: '2px solid rgba(63, 81, 181, 0.1)',
              }}>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', width: '35%' }}>Utilisateur</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', width: '35%' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', width: '15%' }}>Statut</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', width: '15%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                                      <TableRow 
                    key={user.id} 
                    hover 
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#f1f5f9',
                        transition: 'all 0.2s ease'
                      },
                      borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                      cursor: 'pointer'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {getUserAvatar(user)}
                        <Box>
                          <Typography variant="body1" fontWeight={600}>
                            {user.displayName || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {user.id.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.isAdmin ? (
                        <Chip 
                          icon={<AdminPanelSettingsIcon />}
                          label="Administrateur" 
                          sx={{
                            background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
                            color: 'white',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)'
                          }}
                        />
                      ) : (
                        <Chip 
                          icon={<PersonIcon />}
                          label="Utilisateur" 
                          variant="outlined"
                          sx={{
                            borderColor: '#9e9e9e',
                            color: '#666',
                            fontWeight: 500
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {user.id !== currentUser?.uid && (
                          <>
                            <Tooltip title={user.isAdmin ? "Retirer les droits admin" : "Accorder les droits admin"}>
                              <IconButton
                                onClick={() => handleToggleAdmin(user)}
                                sx={{
                                  background: user.isAdmin 
                                    ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                                    : 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                                  color: 'white',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                                  }
                                }}
                              >
                                <SecurityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer l'utilisateur">
                              <IconButton
                                onClick={() => handleDeleteUser(user)}
                                sx={{
                                  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                                  color: 'white',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 6px 16px rgba(244, 67, 54, 0.4)'
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {user.id === currentUser?.uid && (
                          <Tooltip title="Vous ne pouvez pas modifier votre propre compte">
                            <Chip
                              label="Vous-même"
                              variant="outlined"
                              sx={{
                                background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.1) 0%, rgba(92, 107, 192, 0.05) 100%)',
                                border: '1px solid rgba(63, 81, 181, 0.3)',
                                color: '#3F51B5',
                                fontWeight: 600
                              }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
              ))}
              
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <GroupIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Aucun utilisateur trouvé
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Essayez de modifier votre recherche
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          
          <Box sx={{ 
            background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,255,0.9) 100%)',
            backdropFilter: 'blur(10px)'
          }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: 3,
                  paddingRight: 3,
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontWeight: 500,
                  color: '#3F51B5'
                }
              }}
            />
          </Box>
        </TableContainer>
      </Box>
      
      {/* Dialog de confirmation */}
      <BaseDialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        title={
          confirmDialog.type === 'admin'
            ? (confirmDialog.isAdmin ? "👑 Accorder les droits admin" : "👤 Retirer les droits admin")
            : "🗑️ Supprimer l'utilisateur"
        }
        subtitle="Modification des permissions utilisateur"
        maxWidth="sm"
        actions={
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
            <BaseButton 
              onClick={handleCloseConfirmDialog}
              variant="outlined"
              sx={{ px: 4, py: 1 }}
            >
              Annuler
            </BaseButton>
            <BaseButton 
              onClick={handleConfirmAction}
              variant="contained"
              sx={{ 
                px: 4, 
                py: 1,
                background: confirmDialog.type === 'delete'
                  ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
                  : confirmDialog.isAdmin 
                    ? 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)'
                    : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                '&:hover': {
                  background: confirmDialog.type === 'delete'
                    ? 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)'
                    : confirmDialog.isAdmin 
                      ? 'linear-gradient(135deg, #303F9F 0%, #3F51B5 100%)'
                      : 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                }
              }}
            >
              {confirmDialog.type === 'delete' ? 'Supprimer' : 'Confirmer'}
            </BaseButton>
          </Box>
        }
        customBackgroundContent="#f8faff"
      >
        <Box sx={{ 
          textAlign: 'center',
          py: 2,
          background: 'linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(248,250,255,0.9) 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(63, 81, 181, 0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#3F51B5' }}>
            {confirmDialog.type === 'admin'
              ? (confirmDialog.isAdmin ? '🔐 Accordez-vous les droits ?' : '🔓 Retirez-vous les droits ?')
              : '⚠️ Confirmer la suppression'
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {confirmDialog.type === 'admin'
              ? (confirmDialog.isAdmin 
                  ? `Donner les droits d'administrateur à ${confirmDialog.username} ?` 
                  : `Retirer les droits d'administrateur de ${confirmDialog.username} ?`)
              : `Supprimer définitivement l'utilisateur ${confirmDialog.username} ?`
            }<br/>
            <strong>
              {confirmDialog.type === 'admin'
                ? 'Cette action modifiera les permissions de l\'utilisateur.'
                : 'Cette action est irréversible et supprimera toutes les données associées.'
              }
            </strong>
          </Typography>
        </Box>
      </BaseDialog>
    </Box>
  );
};

export default UserManagement; 